import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Palette, Upload, X, MessageCircle, ShieldAlert, Check, Clock, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { createCoverRequest, getUserCoverRequests, uploadEpkImage, resolveAsset } from "@/lib/afmApi";
import { StatusBadge, COVER_REQUEST_STATUS } from "@/components/StatusBadge";
import { SectionTitle } from "@/components/common";
import { fmtDate } from "@/lib/format";

const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none transition-colors focus:border-black";
const GENRES = ["Urbano", "Reggaetón", "Trap", "Pop", "Hip-Hop", "R&B", "Rock", "Electrónica", "Salsa", "Bachata", "Balada", "Folklore", "Jazz", "Clásica", "Otro"];

const TIERS = [
  { key: "basic", name: "Basic Cover", price: "$5 USD", eta: "24h", desc: "Composiciones tipográficas y fotomontajes esenciales." },
  { key: "standard", name: "Standard Cover", price: "$10 USD", eta: "24h", desc: "Retoque fotográfico profesional, edición digital y composición de medio nivel." },
  { key: "advanced", name: "Advanced Art", price: "$15 USD", eta: "24h", desc: "Concepto visual completo, ilustración/manipulación de alta gama y acabado profesional." },
];

const PAY = [
  { key: "pago_movil", label: "Pago Móvil (Bs. tasa oficial)", lines: ["Banco: Banesco", "Teléfono: 04142335584", "Cédula / RIF: 27279118"] },
  { key: "paypal", label: "PayPal", lines: ["hugopernia21@gmail.com"] },
  { key: "binance", label: "Binance Pay", lines: ["fulgencioantonio50@gmail.com"] },
];

const toB64 = (file) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result.split(",")[1]); r.readAsDataURL(file); });

export default function CoverRequest() {
  const { user } = useAuth();
  const [f, setF] = useState({ title: "", main_artist: user?.artist_stage_name || user?.name || "", featuring: "", genre: "Urbano", concept: "" });
  const [refs, setRefs] = useState([]);
  const [tier, setTier] = useState("");
  const [payOpen, setPayOpen] = useState(false);
  const [pay, setPay] = useState({ method: "pago_movil", reference: "", proof: "" });
  const [uploading, setUploading] = useState("");
  const [saving, setSaving] = useState(false);
  const [list, setList] = useState([]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const loadList = useCallback(() => getUserCoverRequests(user.id).then((d) => setList(Array.isArray(d) ? d : [])).catch(() => {}), [user.id]);
  useEffect(() => { loadList(); }, [loadList]);

  const uploadImage = async (file, kind) => {
    if (!file) return;
    if (kind === "ref" && refs.length >= 3) { toast.error("Máximo 3 referencias"); return; }
    setUploading(kind);
    try {
      const b64 = await toB64(file);
      const up = await uploadEpkImage(user.id, file.type, b64);
      const url = resolveAsset(up?.url);
      if (!url) throw new Error();
      if (kind === "ref") setRefs((p) => [...p, url]);
      else setPay((p) => ({ ...p, proof: url }));
    } catch { toast.error("No se pudo subir la imagen"); } finally { setUploading(""); }
  };

  const briefingValid = f.title.trim() && f.main_artist.trim();
  const waUrl = `https://wa.me/584222002957?text=${encodeURIComponent(`Hola equipo de Always Fresh Music, quiero solicitar un diseño Custom Visual & VIP Design. Título: ${f.title || "(sin título)"} - Artista: ${f.main_artist || ""}`)}`;

  const proceed = () => {
    if (!tier) { toast.error("Elige un plan"); return; }
    if (!briefingValid) { toast.error("Completa título y artista"); return; }
    setPayOpen(true);
  };

  const confirm = async () => {
    if (!pay.reference.trim()) { toast.error("El número de referencia es obligatorio"); return; }
    if (!pay.proof) { toast.error("Adjunta la captura del pago"); return; }
    setSaving(true);
    try {
      await createCoverRequest({
        user_id: user.id, user_name: user.name || "", user_email: user.email || "",
        title: f.title.trim(), main_artist: f.main_artist.trim(), featuring: f.featuring.trim(),
        genre: f.genre, concept: f.concept.trim(), reference_urls: refs, tier,
        payment_method: pay.method, payment_reference: pay.reference.trim(), payment_proof_url: pay.proof,
      });
      toast.success("Solicitud enviada. Verificaremos tu pago y comenzaremos el diseño.");
      setPayOpen(false); setTier(""); setRefs([]); setPay({ method: "pago_movil", reference: "", proof: "" }); set("concept", "");
      loadList();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo enviar la solicitud"); } finally { setSaving(false); }
  };

  const selectedPay = PAY.find((p) => p.key === pay.method);

  return (
    <div className="space-y-8" data-testid="cover-request-page">
      <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-[#AC3333]/5 to-white p-6 dark:border-white/10 dark:from-[#AC3333]/15 dark:to-[#171717]">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#AC3333] text-white"><Palette className="h-6 w-6" strokeWidth={1.5} /></div>
          <div>
            <h2 className="font-serif text-xl font-bold text-black">¿No tienes portada? Solicítala a nuestro equipo creativo</h2>
            <p className="mt-1 text-sm text-slate-600">Diseños profesionales listos para tiendas en 24h. Elige un plan, cuéntanos tu idea y procesamos tu pago.</p>
          </div>
        </div>
      </div>

      {/* QC rules */}
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-5" data-testid="cover-qc-rules">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-900"><ShieldAlert className="h-4 w-4" /> Reglas de control de calidad (anti-rechazo)</div>
        <ul className="space-y-1.5 text-xs text-amber-800">
          <li>• <strong>Coincidencia estricta:</strong> si la portada lleva texto, el título y los nombres (artista y feats) deben coincidir 100% con los metadatos.</li>
          <li>• <strong>Sin elementos comerciales:</strong> prohibido @usuarios, webs, logos de tiendas (Spotify, Apple…), precios o fechas de lanzamiento.</li>
          <li>• <strong>Arte limpio recomendado:</strong> se permite portada solo con imagen (sin texto) si la calidad técnica es óptima.</li>
        </ul>
      </div>

      {/* Tiers */}
      <div>
        <SectionTitle>Planes de diseño</SectionTitle>
        <div className="grid gap-4 sm:grid-cols-3">
          {TIERS.map((t) => (
            <button
              key={t.key}
              data-testid={`tier-${t.key}`}
              onClick={() => setTier(t.key)}
              className={`rounded-xl border p-5 text-left transition-all ${tier === t.key ? "border-[#AC3333] bg-[#AC3333]/5 ring-1 ring-[#AC3333]" : "border-slate-200 bg-white hover:border-slate-400"}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-serif text-lg font-bold text-black">{t.name}</span>
                {tier === t.key && <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#AC3333] text-white"><Check className="h-3 w-3" /></span>}
              </div>
              <div className="mt-1 flex items-baseline gap-2"><span className="text-2xl font-bold tabular-nums text-[#AC3333]">{t.price}</span><span className="text-xs text-slate-400">/ {t.eta}</span></div>
              <p className="mt-2 text-xs text-slate-500">{t.desc}</p>
            </button>
          ))}
        </div>
        {/* VIP */}
        <div className="mt-4 flex flex-col items-start justify-between gap-4 rounded-xl border border-black bg-black p-5 text-white sm:flex-row sm:items-center" data-testid="tier-vip">
          <div>
            <div className="font-serif text-lg font-bold">Custom Visual & VIP Design · <span className="text-[#FF6B6B]">$100+ USD</span></div>
            <p className="mt-1 text-xs text-white/70">Visualisers, Spotify Canvas, arte completo y contenido audiovisual 1:1 con el equipo creativo de Always Fresh.</p>
          </div>
          <a data-testid="vip-whatsapp" href={waUrl} target="_blank" rel="noreferrer" className="flex shrink-0 items-center gap-2 rounded-lg bg-[#25D366] px-5 py-3 text-sm font-semibold text-black transition-opacity hover:opacity-90">
            <MessageCircle className="h-4 w-4" /> Chat with Designer on WhatsApp
          </a>
        </div>
      </div>

      {/* Briefing */}
      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <SectionTitle>Briefing del diseño</SectionTitle>
        <div className="grid gap-5 sm:grid-cols-2">
          <div><label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Song / Album Title <span className="text-[#AC3333]">*</span></label><input data-testid="cr-title" value={f.title} onChange={(e) => set("title", e.target.value)} className={inputCls} placeholder="Título exacto de la obra" /></div>
          <div><label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Main Artist Name <span className="text-[#AC3333]">*</span></label><input data-testid="cr-artist" value={f.main_artist} onChange={(e) => set("main_artist", e.target.value)} className={inputCls} /></div>
          <div><label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Featuring / Collaborators</label><input data-testid="cr-featuring" value={f.featuring} onChange={(e) => set("featuring", e.target.value)} className={inputCls} placeholder="ft. Artista B (opcional)" /></div>
          <div><label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Genre</label><select data-testid="cr-genre" value={f.genre} onChange={(e) => set("genre", e.target.value)} className={inputCls}>{GENRES.map((g) => <option key={g} value={g}>{g}</option>)}</select></div>
        </div>
        <div className="mt-5"><label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Concept Description</label><textarea data-testid="cr-concept" value={f.concept} onChange={(e) => set("concept", e.target.value)} rows={3} className={`${inputCls} resize-none`} placeholder="Describe la idea visual, colores, estilo, referencias culturales…" /></div>
        <div className="mt-5">
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">References (hasta 3)</label>
          <div className="flex flex-wrap items-center gap-3">
            {refs.map((u, i) => (
              <div key={i} className="relative h-20 w-20 overflow-hidden rounded-lg border border-slate-200">
                <img src={u} alt={`ref-${i}`} className="h-full w-full object-cover" />
                <button data-testid={`cr-ref-remove-${i}`} onClick={() => setRefs((p) => p.filter((_, j) => j !== i))} className="absolute right-0.5 top-0.5 rounded-full bg-black/60 p-0.5 text-white"><X className="h-3 w-3" /></button>
              </div>
            ))}
            {refs.length < 3 && (
              <label data-testid="cr-ref-upload" className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-300 text-slate-400 hover:border-black">
                {uploading === "ref" ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-black" /> : <Upload className="h-5 w-5" />}
                <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0], "ref")} />
              </label>
            )}
          </div>
        </div>
        {tier && TIERS.find((t) => t.key === tier) && (
          <button data-testid="proceed-payment" onClick={proceed} className="mt-6 flex items-center gap-2 rounded-lg bg-[#AC3333] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#FF0505]">
            Proceed to Payment · {TIERS.find((t) => t.key === tier).price}
          </button>
        )}
        {!tier && <p className="mt-6 text-xs text-slate-400">Selecciona un plan arriba para continuar al pago.</p>}
      </div>

      {/* My requests */}
      {list.length > 0 && (
        <div data-testid="cr-my-requests">
          <SectionTitle>Mis solicitudes</SectionTitle>
          <div className="space-y-3">
            {list.map((r) => (
              <div key={r.id} data-testid={`cr-item-${r.id}`} className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-400"><Palette className="h-5 w-5" /></div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2"><span className="truncate font-semibold text-black">{r.title}</span><StatusBadge map={COVER_REQUEST_STATUS} status={r.status} /></div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-slate-500"><span>{r.tier_label}</span><span>· ${r.amount_usd}</span><span>· {fmtDate(r.created_at)}</span><span className="flex items-center gap-1"><Clock className="h-3 w-3" /> Ref {r.payment_reference}</span></div>
                  {r.status === "rejected" && r.admin_note && <p className="mt-1 text-xs text-red-600">Motivo: {r.admin_note}</p>}
                </div>
                {r.status === "delivered" && r.delivered_url && <a href={r.delivered_url} target="_blank" rel="noreferrer" className="flex shrink-0 items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white"><ExternalLink className="h-3.5 w-3.5" /> Descargar</a>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payment modal */}
      {payOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="payment-modal">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setPayOpen(false)} />
          <div className="relative z-10 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
              <div>
                <h3 className="font-serif text-lg font-bold text-black">Proceed to Payment</h3>
                <p className="text-xs text-slate-500">{TIERS.find((t) => t.key === tier)?.name} · {TIERS.find((t) => t.key === tier)?.price}</p>
              </div>
              <button data-testid="payment-close" onClick={() => setPayOpen(false)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-black"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-5 px-6 py-5">
              <div className="flex gap-2">
                {PAY.map((p) => (
                  <button key={p.key} data-testid={`pay-method-${p.key}`} onClick={() => setPay((s) => ({ ...s, method: p.key }))} className={`flex-1 rounded-lg border px-3 py-2 text-xs font-semibold transition-colors ${pay.method === p.key ? "border-black bg-black text-white" : "border-slate-300 text-slate-600 hover:border-black"}`}>{p.label.split(" (")[0]}</button>
                ))}
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="pay-details">
                <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">{selectedPay?.label}</div>
                {selectedPay?.lines.map((l) => <div key={l} className="font-mono text-sm text-black">{l}</div>)}
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Número de Referencia / ID de Transacción <span className="text-[#AC3333]">*</span></label>
                <input data-testid="pay-reference" value={pay.reference} onChange={(e) => setPay((s) => ({ ...s, reference: e.target.value }))} className={inputCls} placeholder="Ej. 0123456789" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Captura del pago <span className="text-[#AC3333]">*</span></label>
                {pay.proof ? (
                  <div className="relative inline-block">
                    <img src={pay.proof} alt="comprobante" className="h-28 rounded-lg border border-slate-200 object-cover" />
                    <button data-testid="pay-proof-remove" onClick={() => setPay((s) => ({ ...s, proof: "" }))} className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white"><X className="h-3 w-3" /></button>
                  </div>
                ) : (
                  <label data-testid="pay-proof-upload" className="flex h-28 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-300 text-slate-400 hover:border-black">
                    {uploading === "proof" ? <span className="h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-black" /> : <><Upload className="h-5 w-5" /><span className="mt-1 text-xs">Sube la captura del comprobante</span></>}
                    <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0], "proof")} />
                  </label>
                )}
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-slate-100 bg-slate-50/60 px-6 py-4">
              <button onClick={() => setPayOpen(false)} className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium hover:border-black">Cancelar</button>
              <button data-testid="payment-confirm" onClick={confirm} disabled={saving} className="flex items-center gap-2 rounded-lg bg-[#AC3333] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#FF0505] disabled:opacity-60">
                {saving ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" /> : <Check className="h-4 w-4" />} Confirmar solicitud
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
